import React, { useRef } from 'react';

const containsTarget = (
  root: HTMLElement | null,
  target: EventTarget | null,
): boolean => {
  return Boolean(root && target instanceof Node && root.contains(target));
};

/** Ref or mutable ref — only `current` is read for outside-click checks. */
type ElementRootRef = { readonly current: HTMLElement | null };

export const useOnClickOutside = ({
  ref,
  handleClickOutside,
  extraRoots,
}: {
  ref?: React.RefObject<HTMLElement>;
  handleClickOutside: (e: MouseEvent) => void;
  /**
   * Additional DOM subtrees that count as “inside” (e.g. portaled floating menus).
   */
  extraRoots?: ReadonlyArray<ElementRootRef>;
}): React.RefObject<HTMLDivElement> => {
  const containerRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target;
      const primaryRoot = ref?.current ?? containerRef.current;

      if (containsTarget(primaryRoot, target)) {
        return;
      }

      if (
        extraRoots?.some((rootRef) => containsTarget(rootRef.current, target))
      ) {
        return;
      }

      handleClickOutside(event);
    };

    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [extraRoots, handleClickOutside, ref]);

  return containerRef;
};
